import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileCheck2, KeyRound, Mail, Send, ShieldCheck, ChevronRight, ChevronLeft, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CERT_TYPES = ["Experience", "Conduct", "Recommendation", "Training", "Achievement"];
const TODAY = () => new Date().toISOString().slice(0, 10);

export default function IssueCertificatePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const presetRecord = params.get("record");

  // Step: 1 employee · 2 type · 3 details · 4 approval
  const [step, setStep] = useState(presetRecord ? 2 : 1);
  const [record, setRecord] = useState(null);
  const [taxonomy, setTaxonomy] = useState({});
  const [conductCategories, setConductCategories] = useState([]);
  const [ratingScale, setRatingScale] = useState([]);
  const [form, setForm] = useState({
    cert_type: "Experience",
    sub_category: "Full-Time Employment",
    title: "",
    description: "",
    start_date: TODAY(),
    end_date: TODAY(),
    issue_date: TODAY(),
    rehire: "",
    recommend_to_others: "",
    recommendation_level: "",
    conduct_ratings: [],
  });
  const [conductPreview, setConductPreview] = useState({ score: null, grade: null, summary: null });
  const [duration, setDuration] = useState({ text: "" });
  const [draft, setDraft] = useState(null);
  const [otp, setOtp] = useState("");
  const [mockOtp, setMockOtp] = useState(null);
  const [pin, setPin] = useState("");
  const [employees, setEmployees] = useState([]);
  const [postExperiencePrompt, setPostExperiencePrompt] = useState(null);

  useEffect(() => {
    api.get("/certificates/v2/taxonomy").then((r) => {
      setTaxonomy(r.data.taxonomy);
      setConductCategories(r.data.conduct_categories || []);
      setRatingScale(r.data.rating_scale || []);
    });
    api.get("/records/employees").then((r) => setEmployees(r.data.rows));
  }, []);

  useEffect(() => {
    if (presetRecord && employees.length) {
      const found = employees.find((e) => e.id === presetRecord);
      if (found) setRecord(found);
    }
  }, [presetRecord, employees]);

  useEffect(() => {
    if (form.start_date) {
      api.post("/certificates/v2/duration", { start_date: form.start_date, end_date: form.end_date })
        .then((r) => setDuration(r.data)).catch(() => {});
    }
  }, [form.start_date, form.end_date]);

  // Live conduct score preview
  useEffect(() => {
    if (form.cert_type === "Conduct" && form.conduct_ratings.length > 0) {
      api.post("/certificates/v2/conduct-preview", { conduct_ratings: form.conduct_ratings })
        .then((r) => setConductPreview(r.data)).catch(() => {});
    } else if (form.cert_type !== "Conduct") {
      setConductPreview({ score: null, grade: null, summary: null });
    }
  }, [form.cert_type, form.conduct_ratings]);

  const subCats = taxonomy[form.cert_type] || [];
  const isExperience = form.cert_type === "Experience";
  const isFullTime = isExperience && form.sub_category === "Full-Time Employment";

  const goNext = () => setStep((s) => Math.min(4, s + 1));
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const createDraft = async () => {
    if (!record) return toast.error("Choose an employee first.");
    if (!form.title.trim()) return toast.error("Title is required.");
    try {
      const { data } = await api.post("/certificates/v2/draft", {
        employee_record_id: record.id,
        cert_type: form.cert_type,
        sub_category: form.sub_category,
        title: form.title,
        description: form.description,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        issue_date: form.issue_date || null,
        rehire: form.rehire || null,
        recommend_to_others: form.recommend_to_others || null,
        recommendation_level: form.recommendation_level || null,
        conduct_ratings: form.cert_type === "Conduct" ? form.conduct_ratings : null,
      });
      setDraft(data);
      toast.success("Draft created.");
      goNext();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const afterIssued = (justIssued) => {
    // F5 — if we just issued an Experience cert, prompt to issue a Conduct cert.
    if (justIssued?.cert_type === "Experience") {
      setPostExperiencePrompt(justIssued);
    } else {
      nav("/certificates");
    }
  };

  const onConductFromPrompt = () => {
    // Reset wizard for Conduct using same employee + duration
    const sameRecord = record;
    setPostExperiencePrompt(null);
    setDraft(null);
    setForm((p) => ({
      ...p,
      cert_type: "Conduct",
      sub_category: "Professional Conduct",
      title: `Conduct certificate for ${sameRecord?.full_name || ""}`.trim(),
      conduct_ratings: [],
      rehire: "",
      recommend_to_others: "",
      recommendation_level: "",
    }));
    setStep(2);
  };

  const requestOtp = async () => {
    try {
      const { data } = await api.post(`/certificates/v2/${draft.id}/request-otp`);
      setMockOtp(data.otp_mock);
      toast.success(`OTP sent to ${draft.issuer_org}'s HR email (mocked).`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const issueWithOtp = async () => {
    try {
      await api.post(`/certificates/v2/${draft.id}/issue-with-otp`, { otp });
      toast.success("Certificate issued!");
      afterIssued(draft);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const issueWithPin = async () => {
    try {
      await api.post(`/certificates/v2/${draft.id}/issue-with-pin`, { pin });
      toast.success("Certificate issued!");
      afterIssued(draft);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitForApproval = async () => {
    try {
      await api.post(`/certificates/v2/${draft.id}/submit-for-approval`);
      toast.success("Submitted for approval. Approvers have been notified.");
      nav("/workspace/approvals");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <WorkspaceLayout subtitle="HR workspace" title="Issue Verified Certificate">
      <Stepper step={step} />

      {step === 1 && (
        <EmployeePicker employees={employees} onPick={(r) => { setRecord(r); goNext(); }} />
      )}

      {step === 2 && record && (
        <TypeStep
          record={record} form={form} setForm={setForm}
          subCats={subCats} onBack={goPrev} onNext={goNext}
          types={CERT_TYPES}
        />
      )}

      {step === 3 && record && (
        <DetailsStep
          form={form} setForm={setForm} duration={duration} isExperience={isExperience}
          isConduct={form.cert_type === "Conduct"}
          conductCategories={conductCategories}
          ratingScale={ratingScale}
          conductPreview={conductPreview}
          onBack={goPrev} onNext={createDraft}
        />
      )}

      {step === 4 && draft && (
        <ApprovalStep
          draft={draft} isFullTime={isFullTime}
          otp={otp} setOtp={setOtp} mockOtp={mockOtp}
          pin={pin} setPin={setPin}
          onRequestOtp={requestOtp} onIssueOtp={issueWithOtp}
          onIssuePin={issueWithPin} onSubmitApproval={submitForApproval}
          onBack={goPrev}
        />
      )}

      {postExperiencePrompt && (
        <PostExperienceModal
          cert={postExperiencePrompt}
          onConduct={onConductFromPrompt}
          onSkip={() => { setPostExperiencePrompt(null); nav("/certificates"); }}
        />
      )}
    </WorkspaceLayout>
  );
}

function Stepper({ step }) {
  const labels = ["Choose employee", "Type & sub-category", "Details", "Approval"];
  return (
    <div className="flex flex-wrap gap-0.5 mb-6" data-testid="issue-stepper">
      {labels.map((l, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={l} className={`flex-1 min-w-[160px] px-3 py-2.5 border-l-2 ${active ? "border-ec-emerald bg-ec-emerald/5" : done ? "border-ec-emerald/40 bg-ec-mute" : "border-ec-line bg-white"}`} data-testid={`issue-step-${n}`}>
            <div className="eyebrow text-ec-ink-soft text-[10px]">Step {n}</div>
            <div className={`text-sm font-semibold ${active ? "text-ec-emerald" : "text-ec-ink"}`}>{l}</div>
          </div>
        );
      })}
    </div>
  );
}

function EmployeePicker({ employees, onPick }) {
  const [q, setQ] = useState("");
  const filtered = employees.filter((e) => {
    const s = q.toLowerCase();
    return !s || (e.full_name || "").toLowerCase().includes(s) || (e.employee_id || "").toLowerCase().includes(s) || (e.personal_email || "").toLowerCase().includes(s);
  });
  return (
    <Card className="rounded-sm border-ec-line" data-testid="emp-picker">
      <CardContent className="p-5">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / email / Employee ID" className="rounded-sm h-10 mb-3" data-testid="emp-picker-search" />
        <div className="max-h-[480px] overflow-y-auto border border-ec-line rounded-sm divide-y divide-ec-line">
          {filtered.length === 0 ? <div className="p-6 text-center text-ec-ink-soft text-sm" data-testid="emp-picker-empty">No employees in roster.</div> : filtered.map((e) => (
            <button key={e.id} onClick={() => onPick(e)} className="w-full text-left px-4 py-3 hover:bg-ec-mute flex items-center gap-3" data-testid={`emp-picker-row-${e.id}`}>
              <div className="w-9 h-9 rounded-sm bg-ec-ink text-white flex items-center justify-center font-display font-bold text-sm">{(e.full_name || "?").slice(0,1).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{e.full_name}</div>
                <div className="text-xs text-ec-ink-soft truncate">{e.designation || "—"} · {e.department || "—"} · {e.employment_status}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-ec-ink-soft" />
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TypeStep({ record, form, setForm, subCats, onBack, onNext, types }) {
  const setSubCat = (v) => setForm({ ...form, sub_category: v });
  useEffect(() => {
    // When type changes, default sub_category to the first valid one for that type
    if (subCats.length && !subCats.includes(form.sub_category)) {
      setForm((prev) => ({ ...prev, sub_category: subCats[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cert_type, subCats.length]);
  return (
    <Card className="rounded-sm border-ec-line" data-testid="type-step">
      <CardContent className="p-5 space-y-5">
        <div className="text-sm text-ec-ink-soft">For: <span className="font-semibold text-ec-ink">{record.full_name}</span> · {record.designation || "—"} · {record.employee_id}</div>

        <div>
          <Label className="eyebrow">Certificate type</Label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2" data-testid="cert-type-grid">
            {types.map((t) => (
              <button key={t} type="button" onClick={() => setForm({ ...form, cert_type: t })}
                aria-pressed={form.cert_type === t}
                className={`px-3 py-3 text-sm rounded-sm border ${form.cert_type === t ? "bg-ec-ink text-white border-ec-ink" : "border-ec-line hover:bg-ec-mute"}`}
                data-testid={`cert-type-${t}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="eyebrow">Sub-category</Label>
          <Select value={form.sub_category} onValueChange={setSubCat}>
            <SelectTrigger className="rounded-sm mt-2" data-testid="cert-subcat-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {subCats.map((s) => <SelectItem key={s} value={s} data-testid={`cert-subcat-opt-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" className="rounded-sm gap-2" onClick={onBack} data-testid="type-back"><ChevronLeft className="w-4 h-4" /> Back</Button>
          <Button onClick={onNext} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="type-next">Next <ChevronRight className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailsStep({ form, setForm, duration, isExperience, isConduct, conductCategories, ratingScale, conductPreview, onBack, onNext }) {
  const setRating = (cat, rating) => {
    const next = [...(form.conduct_ratings || []).filter((r) => r.category !== cat), { category: cat, rating }];
    setForm({ ...form, conduct_ratings: next });
  };
  const ratingFor = (cat) => (form.conduct_ratings || []).find((r) => r.category === cat)?.rating;
  return (
    <Card className="rounded-sm border-ec-line" data-testid="details-step">
      <CardContent className="p-5 space-y-5">
        <div>
          <Label className="eyebrow">Title <span className="text-ec-emerald">*</span></Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Senior Software Engineer" className="rounded-sm mt-2" data-testid="cert-title" />
        </div>
        <div>
          <Label className="eyebrow">Description</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the certificate / role / training…" rows={3} className="rounded-sm mt-2" data-testid="cert-desc" />
        </div>
        {!isConduct && (
          <>
            <div className="grid sm:grid-cols-3 gap-4">
              <DateField label="Start Date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="cert-start" />
              <DateField label="End Date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="cert-end" />
              <DateField label="Issue Date" value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testid="cert-issue" />
            </div>
            <div className="px-4 py-3 bg-ec-emerald/5 border border-ec-emerald/30 rounded-sm flex flex-wrap gap-3 items-center" data-testid="duration-banner">
              <span className="eyebrow text-ec-emerald">Total verified duration</span>
              <span className="font-display text-xl font-black text-ec-ink">{duration?.text || "—"}</span>
              {duration?.years !== undefined && <span className="text-xs font-mono-plex text-ec-ink-soft">({duration.years || 0}Y · {duration.months || 0}M · {duration.days || 0}D)</span>}
            </div>
          </>
        )}
        {isConduct && (
          <div className="grid sm:grid-cols-1 gap-4">
            <DateField label="Issue Date" value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testid="cert-issue" />
          </div>
        )}

        {isExperience && (
          <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-ec-line">
            <Label className="eyebrow sm:col-span-3 text-ec-emerald">Rehiring recommendation</Label>
            <PickField label="Would you rehire?" value={form.rehire} options={["Yes", "Maybe", "No"]} onChange={(v) => setForm({ ...form, rehire: v })} testid="cert-rehire" />
            <PickField label="Recommend to others?" value={form.recommend_to_others} options={["Yes", "Maybe", "No"]} onChange={(v) => setForm({ ...form, recommend_to_others: v })} testid="cert-recommend-others" />
            <PickField label="Overall level" value={form.recommendation_level} options={["Highly Recommended", "Recommended", "Neutral", "Not Recommended"]} onChange={(v) => setForm({ ...form, recommendation_level: v })} testid="cert-rec-level" />
          </div>
        )}

        {isConduct && (
          <div className="pt-4 border-t border-ec-line space-y-4" data-testid="conduct-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="eyebrow text-ec-emerald">Conduct ratings ({form.conduct_ratings?.length || 0} / {conductCategories.length})</Label>
              <div className="text-[11px] text-ec-ink-soft font-mono-plex">Excellent (5) → Needs Improvement (1)</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="conduct-grid">
              {conductCategories.map((cat) => (
                <div key={cat} className="border border-ec-line rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`conduct-row-${cat}`}>
                  <span className="text-sm flex-1 min-w-[120px]">{cat}</span>
                  <div className="flex gap-1 flex-wrap">
                    {ratingScale.map((r) => (
                      <button key={r} type="button"
                        onClick={() => setRating(cat, r)}
                        className={`px-2 py-1 text-[10px] rounded-sm border ${ratingFor(cat) === r ? "bg-ec-ink text-white border-ec-ink" : "border-ec-line hover:bg-ec-mute"}`}
                        data-testid={`conduct-rating-${cat}-${r}`}>
                        {r.split(" ").map(w => w[0]).join("")}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {conductPreview?.grade && (
              <div className="px-4 py-3 bg-ec-emerald/5 border border-ec-emerald/30 rounded-sm flex flex-wrap items-center gap-3" data-testid="conduct-score-banner">
                <span className="eyebrow text-ec-emerald">Live conduct score</span>
                <span className="font-display text-2xl font-black text-ec-ink">{conductPreview.score}%</span>
                <Badge className="rounded-sm bg-ec-emerald text-white">{conductPreview.grade}</Badge>
                <span className="text-xs text-ec-ink-soft flex-1">{conductPreview.summary}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="outline" className="rounded-sm gap-2" onClick={onBack} data-testid="details-back"><ChevronLeft className="w-4 h-4" /> Back</Button>
          <Button onClick={onNext} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="details-next">Save draft & continue <ChevronRight className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PostExperienceModal({ cert, onConduct, onSkip }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="post-exp-modal">
      <Card className="rounded-sm border-ec-line max-w-md w-full">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-lg font-bold">Issue a Conduct Certificate?</h3>
          </div>
          <p className="text-sm text-ec-ink-soft">
            You just issued an Experience certificate for <span className="text-ec-ink font-semibold">{cert.recipient_name}</span>.
            Would you like to additionally issue a Conduct certificate now? This creates a separate verification with its own QR
            and adds powerful trust signals to their EthicCred passport.
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" className="rounded-sm" onClick={onSkip} data-testid="post-exp-skip">Skip</Button>
            <Button onClick={onConduct} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="post-exp-issue-conduct">
              <Star className="w-4 h-4" /> Issue Conduct Certificate
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ApprovalStep({ draft, isFullTime, otp, setOtp, mockOtp, pin, setPin, onRequestOtp, onIssueOtp, onIssuePin, onSubmitApproval, onBack }) {
  return (
    <Card className="rounded-sm border-ec-line" data-testid="approval-step">
      <CardContent className="p-5 space-y-5">
        <div className="px-4 py-3 bg-ec-mute rounded-sm" data-testid="draft-summary">
          <div className="eyebrow text-ec-ink-soft">Draft</div>
          <div className="font-semibold text-ec-ink">{draft.title}</div>
          <div className="text-xs text-ec-ink-soft mt-1">{draft.cert_type} · {draft.sub_category} · for {draft.recipient_name}</div>
          <div className="text-[10px] font-mono-plex text-ec-ink-soft">#{draft.certificate_no}</div>
        </div>

        {isFullTime ? (
          <div className="space-y-3" data-testid="approval-multistep">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4 text-ec-emerald" />
              <span>Full-Time Experience certificates require multi-step approval (HR Manager → Org Admin).</span>
            </div>
            <Button onClick={onSubmitApproval} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="approval-submit">
              <Send className="w-4 h-4" /> Submit for Approval
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6" data-testid="approval-pin-or-otp">
            <div className="border border-ec-line rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2 eyebrow text-ec-emerald"><KeyRound className="w-3.5 h-3.5" /> Option 1: PIN</div>
              <p className="text-xs text-ec-ink-soft mb-2">Use your organisation's 6-digit issuance PIN. Configure it in Settings → Approval.</p>
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" inputMode="numeric" className="rounded-sm tracking-[0.5em] text-center text-lg" data-testid="approval-pin-input" />
              <Button onClick={onIssuePin} className="rounded-sm w-full mt-3 bg-ec-ink hover:bg-zinc-800" disabled={pin.length !== 6} data-testid="approval-pin-issue">Issue Certificate</Button>
            </div>
            <div className="border border-ec-line rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2 eyebrow text-ec-emerald"><Mail className="w-3.5 h-3.5" /> Option 2: Email OTP</div>
              <p className="text-xs text-ec-ink-soft mb-2">OTP is emailed to the issuer (mocked on-screen).</p>
              {!mockOtp ? (
                <Button onClick={onRequestOtp} variant="outline" className="rounded-sm w-full gap-2" data-testid="approval-otp-request"><Mail className="w-3.5 h-3.5" /> Request OTP</Button>
              ) : (
                <>
                  <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900" data-testid="approval-otp-mock">
                    <strong>Mocked OTP:</strong> <span className="font-mono-plex tracking-widest">{mockOtp}</span>
                  </div>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit OTP" inputMode="numeric" className="rounded-sm mt-2 tracking-[0.5em] text-center text-lg" data-testid="approval-otp-input" />
                  <Button onClick={onIssueOtp} className="rounded-sm w-full mt-3 bg-ec-ink hover:bg-zinc-800" disabled={otp.length !== 6} data-testid="approval-otp-issue">Verify & Issue</Button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="outline" className="rounded-sm gap-2" onClick={onBack} data-testid="approval-back"><ChevronLeft className="w-4 h-4" /> Back</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm mt-2" data-testid={testid} />
    </div>
  );
}

function PickField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testid}>
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`px-2.5 py-1.5 text-xs rounded-sm border ${value === o ? "bg-ec-ink text-white border-ec-ink" : "border-ec-line hover:bg-ec-mute"}`}
            data-testid={`${testid}-${o.replace(/\s+/g, "-")}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
